<template>
  <canvas
    ref="canvas"
    class="item-thumbnail"
    width="192"
    height="192"
    role="img"
    :aria-label="title"
  ></canvas>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue"
import { ACCESSORY_MODELS, type VoxelPart } from "../shared/accessoryModels"

const props = defineProps<{ itemId: string; title: string }>()
const canvas = ref<HTMLCanvasElement | null>(null)

type Point = { x: number; y: number }
type Box = {
  vertices: Point[]
  depth: number
  color: number
  opacity: number
}

const palette = { skin: 0xe0ac80, hair: 0x292b33, clothes: 0x6258d9 }

function colorOf(part: VoxelPart): number {
  if (part.usePalette) return palette[part.usePalette]
  return part.color ?? 0x6258d9
}

function shade(color: number, factor: number): string {
  const r = Math.max(0, Math.min(255, Math.round(((color >> 16) & 255) * factor)))
  const g = Math.max(0, Math.min(255, Math.round(((color >> 8) & 255) * factor)))
  const b = Math.max(0, Math.min(255, Math.round((color & 255) * factor)))
  return `rgb(${r}, ${g}, ${b})`
}

function project(x: number, y: number, z: number): Point {
  return { x: (x - z) * 0.866, y: -y + (x + z) * 0.5 }
}

function boxFrom(part: VoxelPart, scale: number, ox: number, oy: number, oz: number): Box {
  const x = (part.x + ox) * scale
  const y = (part.y + oy) * scale
  const z = (part.z + oz) * scale
  const hw = (part.w * scale) / 2
  const hh = (part.h * scale) / 2
  const hd = (part.d * scale) / 2
  const raw = [
    [x - hw, y - hh, z - hd], [x + hw, y - hh, z - hd],
    [x + hw, y + hh, z - hd], [x - hw, y + hh, z - hd],
    [x - hw, y - hh, z + hd], [x + hw, y - hh, z + hd],
    [x + hw, y + hh, z + hd], [x - hw, y + hh, z + hd],
  ]
  return {
    vertices: raw.map(([px, py, pz]) => project(px, py, pz)),
    depth: x + z + y * 0.05,
    color: colorOf(part),
    opacity: part.opacity ?? 1,
  }
}

function polygon(ctx: CanvasRenderingContext2D, points: Point[], fill: string, opacity: number) {
  ctx.beginPath()
  ctx.moveTo(points[0].x, points[0].y)
  for (const point of points.slice(1)) ctx.lineTo(point.x, point.y)
  ctx.closePath()
  ctx.globalAlpha = opacity
  ctx.fillStyle = fill
  ctx.fill()
  ctx.strokeStyle = "rgba(41, 43, 51, 0.12)"
  ctx.lineWidth = 0.012
  ctx.stroke()
}

let renderVersion = 0
async function render() {
  const version = ++renderVersion
  const el = canvas.value
  const model = ACCESSORY_MODELS[props.itemId]
  if (!el) return
  if (props.itemId.startsWith("room_")) {
    const itemId = props.itemId
    const { renderRoomItemThumbnail } = await import("../shared/roomThumbnailRenderer")
    if (version === renderVersion && canvas.value === el && props.itemId === itemId) renderRoomItemThumbnail(el, itemId)
    return
  }
  const ctx = el.getContext("2d")
  if (!ctx) return
  if (!model) return

  const modelScale = model.scale ?? 1
  const boxes = model.parts.map((part) => boxFrom(
    part,
    modelScale,
    model.xOffset ?? 0,
    model.yOffset ?? 0,
    model.zOffset ?? 0,
  )).sort((a, b) => a.depth - b.depth)
  const points = boxes.flatMap((box) => box.vertices)
  const minX = Math.min(...points.map((point) => point.x))
  const maxX = Math.max(...points.map((point) => point.x))
  const minY = Math.min(...points.map((point) => point.y))
  const maxY = Math.max(...points.map((point) => point.y))
  const width = Math.max(0.1, maxX - minX)
  const height = Math.max(0.1, maxY - minY)
  const padding = 22
  const fit = Math.min((el.width - padding * 2) / width, (el.height - padding * 2) / height)

  ctx.clearRect(0, 0, el.width, el.height)
  ctx.save()
  ctx.translate(el.width / 2 - ((minX + maxX) / 2) * fit, el.height / 2 - ((minY + maxY) / 2) * fit)
  ctx.scale(fit, fit)
  for (const box of boxes) {
    const v = box.vertices
    polygon(ctx, [v[3], v[2], v[6], v[7]], shade(box.color, 1.12), box.opacity)
    polygon(ctx, [v[1], v[5], v[6], v[2]], shade(box.color, 0.84), box.opacity)
    polygon(ctx, [v[4], v[5], v[6], v[7]], shade(box.color, 0.96), box.opacity)
  }
  ctx.restore()
  ctx.globalAlpha = 1
}

onMounted(render)
watch(() => props.itemId, render)
</script>

<style scoped>
.item-thumbnail {
  display: block;
  width: 88px;
  height: 88px;
  border-radius: 22px;
  background: linear-gradient(155deg, #f6f2e9 0%, #e8e5f4 100%);
}
</style>
